import { ClinicalTrial, CreateInquiryVM, Ecrf, Inquiry, InquiryHistoryPage, Patient, PatientAdmission, SaveFormDataVM, FormData, AuditTrailPage } from "./models";

export const getPatientAdmission: () => Promise<Array<PatientAdmission>> = async () => {
    const response = await fetch("/api/PatientAdmissions", {
        method: "GET",
        headers: {
            Accept: "application/json",
        },
    });

    if (response.status === 200) {
        const info: Array<PatientAdmission> = await response.json();
        return info;
    }

    return [] as Array<PatientAdmission>;
}

export const getPatients: () => Promise<Array<Patient>> = async () => {
    const response = await fetch("/api/Patient", {
        method: "GET",
        headers: {
            Accept: "application/json",
        },
    });

    if (response.status === 200) {
        const info: Array<Patient> = await response.json();
        return info;
    }

    return [] as Array<Patient>;
}

/**
 * Result of a patient save. `error` carries the SERVER's message.
 *
 * This used to return a bare boolean, so every failure surfaced in the UI as
 * one generic sentence: a duplicate patient code, a missing field and a server
 * fault were indistinguishable, and the investigator had no idea what to fix.
 * The controller already returns a specific Greek message for each case - it
 * was simply being discarded here.
 */
export interface SaveResult {
    ok: boolean;
    error?: string;
}

export const postPatient: (patient: Patient) => Promise<SaveResult> = async (patient: Patient) => {
    const response = await fetch("/api/Patient/Add", {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
        body: JSON.stringify(patient)
    });

    if (response.ok) {
        return { ok: true };
    }

    // BadRequest bodies come back either as a bare JSON string (our own
    // messages) or as an ASP.NET ValidationProblemDetails object when model
    // binding rejects the payload before the action runs. Both are worth
    // showing; the second is what a genuine client/server mismatch looks like.
    let error: string | undefined;
    try {
        const text = await response.text();
        if (text) {
            try {
                const parsed = JSON.parse(text);
                if (typeof parsed === "string") {
                    error = parsed;
                }
                else if (parsed?.errors && typeof parsed.errors === "object") {
                    error = Object.values(parsed.errors).flat().join(" ");
                }
                else {
                    error = parsed?.title ?? parsed?.detail ?? text;
                }
            }
            catch {
                error = text;
            }
        }
    }
    catch {
        // Body already consumed or unreadable - fall through to the generic
        // message rather than masking the original failure with a parse error.
    }

    return { ok: false, error: error?.trim() || undefined };
}

export const deletePatient: (patientId: number) => Promise<boolean> = async (patientId: number) => {
    const response = await fetch(`/api/Patient/Delete/${patientId}`, {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
    });

    if (response.status === 200) {
        return true;
    }

    return false;
}

export const postPatientAdmission: (patientAdmission: PatientAdmission) => Promise<boolean> = async (patientAdmission: PatientAdmission) => {
    const response = await fetch("/api/PatientAdmissions/Add", {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
        body: JSON.stringify(patientAdmission)
    });

    if (response.status === 200) {
        const info: number = await response.json();
        return true;
    }

    return false;
}

export const deletePatientAdmission: (patientAdmissionId: number) => Promise<boolean> = async (patientAdmissionId: number) => {
    const response = await fetch(`/api/PatientAdmissions/Delete/${patientAdmissionId}`, {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
    });

    if (response.status === 200) {
        return true;
    }

    return false;
}

export const addClinicalTrial: (trial: ClinicalTrial) => Promise<boolean> = async (trial: ClinicalTrial) => {
    const response = await fetch("/api/ClinicalTrial/Add", {
        method: "POST",
        headers: {
            'Accept': 'application/json',
            'Content-type': 'application/json',
        },
        body: JSON.stringify({ title: trial.title, description: trial.description })
        ,
    });

    if (response.status === 200) {
        return true;
    }

    return false;
}

export const getECRFS: () => Promise<Array<Ecrf>> = async () => {
    const response = await fetch(`/api/Forms`, {
        method: "GET",
        headers: {
            Accept: "application/json",
        },
    });

    if (response.status === 200) {
        const info: Array<Ecrf> = await response.json();
        return info;
    }

    return [] as Array<Ecrf>;
}

export const saveFormValues: (formValues: SaveFormDataVM) => Promise<boolean> = async (formValues: SaveFormDataVM) => {
    const response = await fetch("/api/Forms", {
        method: "POST",
        headers: {
            'Accept': 'application/json',
            'Content-type': 'application/json',
        },
        body: JSON.stringify(formValues),
    });

    if (response.status === 200) {
        return true;
    }

    // Surface why the save failed instead of reporting a silent success.
    // A 400 here is usually model validation rejecting the payload outright.
    let detail = "";
    try { detail = await response.text(); } catch { }
    console.error(`saveFormValues failed: HTTP ${response.status}`, detail);
    throw new Error(`HTTP ${response.status}${detail ? ` - ${detail.slice(0, 300)}` : ""}`);
}

export const getFormValues: (formId: number, sectionId: number, admissionId: number) => Promise<FormData | null> = async (formId: number, sectionId: number, admissionId: number) => {
    const response = await fetch(`/api/Forms/Data?formId=${formId}&sectionId=${sectionId}&admissionId=${admissionId}`, {
        method: "GET",
        headers: {
            'Accept': 'application/json',
            'Content-type': 'application/json',
        },
        body: null,
    });

    if (response.status === 200) {
        try {
            return await response.json();
        } catch {
            return null;
        }
    }

    return null;
}

export const createInquiry = async (vm: CreateInquiryVM): Promise<Inquiry | null> => {
    const response = await fetch("/api/Inquiry", {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(vm),
    });
    if (response.status === 200) return await response.json();
    return null;
};

/**
 * Signs the user out server-side. Returns true when the session was actually
 * cleared, so the caller can avoid redirecting to the login page while the
 * user is in fact still signed in.
 */
export const logout = async (): Promise<boolean> => {
    try {
        const response = await fetch("/api/User/Logout", {
            method: "POST",
            headers: { Accept: "application/json" },
        });
        return response.ok;
    }
    catch {
        return false;
    }
};

export const getMyInquiries = async (): Promise<Inquiry[]> => {
    const response = await fetch("/api/Inquiry/Mine", {
        method: "GET",
        headers: { Accept: "application/json" },
    });
    if (response.status === 200) return await response.json();
    return [];
};

export const getInquiriesForSection = async (formId: number, sectionId: number, patientAdmissionId: number): Promise<Inquiry[]> => {
    const response = await fetch(
        `/api/Inquiry/ForSection?formId=${formId}&sectionId=${sectionId}&patientAdmissionId=${patientAdmissionId}`,
        { method: "GET", headers: { Accept: "application/json" } }
    );
    if (response.status === 200) return await response.json();
    return [];
};

export const resolveInquiry = async (id: number): Promise<boolean> => {
    const response = await fetch(`/api/Inquiry/Resolve/${id}`, {
        method: "POST",
        headers: { Accept: "application/json" },
    });
    return response.status === 200;
};

export const getInquiryHistory = async (skip: number, take: number): Promise<InquiryHistoryPage | null> => {
    const response = await fetch(`/api/Inquiry/History?skip=${skip}&take=${take}`, {
        method: "GET",
        headers: { Accept: "application/json" },
    });
    if (response.status === 200) return await response.json();
    return null;
};


export const getAuditTrail: (skip: number, take: number, userName?: string, action?: string) => Promise<AuditTrailPage | null> = async (skip: number, take: number, userName?: string, action?: string) => {
    const params = new URLSearchParams({
        skip: skip.toString(),
        take: take.toString(),
        ...(userName ? { userName } : {}),
        ...(action ? { action } : {}),
    });
    const response = await fetch(`/api/AuditTrail?${params}`, {
        method: "GET",
        headers: {
            'Accept': 'application/json',
            'Content-type': 'application/json',
        },
        body: null,
    });

    if (response.status === 200) {
        try {
            return await response.json();
        } catch {
            return null;
        }
    }

    return null;
}